import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, Building2, User, Euro, MapPin, FileText, Users, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';

export default function AgencyMandatesForOffer() {
    const [mandates, setMandates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/offers/agency/mandates');
            setMandates(res.data.mandates || []);
        } catch (err) {
            toast.error('Erreur lors du chargement des mandats');
        } finally {
            setLoading(false);
        }
    };

    const filtered = mandates.filter(m => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return m.address?.toLowerCase().includes(q) || m.city?.toLowerCase().includes(q) || m.agent_name?.toLowerCase().includes(q) || m.mandate_number?.toLowerCase().includes(q);
    });

    const ownMandates = filtered.filter(m => m.is_own);
    const otherMandates = filtered.filter(m => !m.is_own);

    const navigate = useNavigate();

    const formatPrice = (price) => {
        if (!price) return '-';
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#FF914D]" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="agency-mandates-for-offer">
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un mandat (adresse, ville, agent, n° mandat)..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/5 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#681793]/50"
                    data-testid="agency-mandates-search"
                />
            </div>

            {/* Other agents' mandates - highlighted */}
            {otherMandates.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#681793]" />
                        Mandats de vos collaborateurs
                        <Badge className="bg-[#681793]/20 text-[#681793] text-[10px]">{otherMandates.length}</Badge>
                    </h3>
                    <div className="space-y-2">
                        {otherMandates.map((m) => (
                            <motion.div
                                key={m.id}
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-4 p-4 rounded-xl bg-[#681793]/5 border border-[#681793]/20 hover:border-[#681793]/40 transition-all cursor-pointer group"
                                onClick={() => navigate(`/agent/mandates/v2/${m.id}`)}
                                data-testid={`agency-mandate-${m.id}`}
                            >
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                                    <Building2 className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className="text-sm font-medium text-foreground truncate">{m.address || 'Adresse non renseignée'}</span>
                                        {m.city && <span className="text-xs text-muted-foreground">{m.city}</span>}
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <User className="w-3 h-3" />
                                            {m.agent_name}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <FileText className="w-3 h-3" />
                                            {m.mandate_number}
                                        </span>
                                        <Badge className={`text-[10px] ${m.mandate_type === 'exclusif' ? 'bg-[#681793]/20 text-[#681793]' : 'bg-white/10 text-white/50'}`}>
                                            {m.mandate_type}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-sm font-bold text-foreground">{formatPrice(m.price)}</p>
                                    <p className="text-[10px] text-[#FF914D]">Faire une offre</p>
                                </div>
                                <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#FF914D] transition-colors shrink-0" />
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {/* Own mandates */}
            {ownMandates.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#FF914D]" />
                        Mes mandats
                        <Badge className="bg-[#FF914D]/20 text-[#FF914D] text-[10px]">{ownMandates.length}</Badge>
                    </h3>
                    <div className="space-y-2">
                        {ownMandates.map((m) => (
                            <div
                                key={m.id}
                                className="flex items-center gap-4 p-3 rounded-xl bg-white/[0.03] border border-border hover:bg-white/[0.05] transition-all cursor-pointer"
                                onClick={() => navigate(`/agent/mandates/v2/${m.id}`)}
                            >
                                <div className="w-8 h-8 rounded-lg bg-[#FF914D]/20 flex items-center justify-center shrink-0">
                                    <FileText className="w-4 h-4 text-[#FF914D]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm text-foreground truncate">{m.address || m.mandate_number}</span>
                                    <span className="text-xs text-muted-foreground ml-2">{m.city}</span>
                                </div>
                                <span className="text-sm font-medium text-foreground">{formatPrice(m.price)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {filtered.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Aucun mandat actif dans l'agence</p>
                </div>
            )}
        </div>
    );
}
